import ExpandableFilterPanel from 'components/molecules/expandable-filter-panel';
import BaseSearchSelect from 'components/atoms/base-search-select';
import BaseSelectBox from 'components/atoms/base-select-box';
import { type ReactNode, type FC } from 'react';
import useUser from 'utils/useUser';
import classNames from 'classnames';
import RoleTypes from 'constants/roleTypes';
import { useTranslation } from 'react-i18next';

import s from './styles.module.scss';
import { useFilter } from './use-filter';

interface ItemProps {
  label: string;
  children: ReactNode;
}
const Item: FC<ItemProps> = ({ label, children }) => (
  <div className={s['item-wrapper']}>
    <label>{label}</label>
    {children}
  </div>
);

interface AgencyUsersFilterProps {}

const AgencyUsersFilter: FC<AgencyUsersFilterProps> = () => {
  const { isAdmin } = useUser();
  const { t } = useTranslation();
  const {
    filter,
    updateFilter,
    searchDoctorLoading,
    searchAgencyLoading,
    statusOptions,
    placeholder,
    debounceSearchDoctor,
    debounceSearchAgency,
    doctorOptions,
    agencyOptions,
    count,
    DEFAULT_DOCTOR_OPTION,
    options,
    resetFilter,
  } = useFilter();
  return (
    <ExpandableFilterPanel
      filtersCount={count}
      onReset={resetFilter}
      isExpanded
    >
      <div className={classNames(s.wrapper, { [s['is-admin']]: isAdmin })}>
        <Item label={t('labels.agencyUserFilter')}>
          <div className={s['user-item']}>
            <BaseSelectBox
              options={options}
              defaultValue={DEFAULT_DOCTOR_OPTION.value}
              className={s.dropdown}
              onChange={(v) => {
                updateFilter({ doctorKey: v });
              }}
              value={filter.doctorKey}
            />
            <BaseSearchSelect
              isLoading={searchDoctorLoading}
              className={s.search}
              options={doctorOptions}
              placeholder={placeholder}
              value={filter.guid}
              onChange={(searchValue) =>
                debounceSearchDoctor({
                  searchKey: filter.doctorKey,
                  searchValue,
                  roleName: filter.roleName,
                  status: filter?.status || '',
                })
              }
              onSelect={(v, option) => {
                updateFilter({
                  page: undefined,
                  guid: v,
                  doctorLabel: option.label as string,
                });
              }}
              onClear={() => {
                updateFilter({
                  page: undefined,
                  guid: undefined,
                  doctorLabel: undefined,
                });
              }}
            />
          </div>
        </Item>
        {isAdmin && (
          <Item label={t('labels.agency')}>
            <BaseSearchSelect
              isLoading={searchAgencyLoading}
              placeholder="Legal Name"
              value={filter.agencyGuid}
              options={agencyOptions}
              onChange={debounceSearchAgency}
              onSelect={(v, opt) => {
                updateFilter({
                  page: undefined,
                  agencyGuid: v,
                  agencyLabel: opt.label as string,
                });
              }}
              onClear={() => {
                updateFilter({
                  page: undefined,
                  agencyGuid: undefined,
                  agencyLabel: undefined,
                });
              }}
            />
          </Item>
        )}
        <Item label={t('labels.role')}>
          <BaseSelectBox
            options={RoleTypes}
            placeholder="Select"
            value={filter.roleName ?? 'all'}
            onChange={(v) => {
              updateFilter({ roleName: v, page: 1 });
            }}
            allowClear
          />
        </Item>
        <Item label={t('labels.status')}>
          <BaseSelectBox
            options={statusOptions}
            placeholder="Select"
            value={filter.status ?? null}
            onChange={(v) => {
              updateFilter({ status: v, page: 1 });
            }}
            allowClear
          />
        </Item>
      </div>
    </ExpandableFilterPanel>
  );
};
export default AgencyUsersFilter;
